import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';

const TipoObjetoModal = ({ isOpen, tipoObjeto, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    id: '',
    descricao: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [isEditing, setIsEditing] = useState(false);

  const token = localStorage.getItem('token');

  // Atualiza formData ao abrir modal ou mudar tipoObjeto
  useEffect(() => {
    if (isOpen) {
      if (tipoObjeto) {
        setFormData({
          id: tipoObjeto.id || '',
          descricao: tipoObjeto.descricao || '',
        });
        setIsEditing(true);
      } else {
        // Limpa completamente o formulário para novo tipo
        setFormData({
          id: '',
          descricao: '',
        });
        setIsEditing(false);
      }
      setError(null);
    }
  }, [isOpen, tipoObjeto]);

  const handleInputChange = useCallback(event => {
    const { name, value } = event.target;

    // Converter descrição para maiúscula
    const processedValue = name === 'descricao' ? value.toUpperCase() : value;

    setFormData(prevData => ({
      ...prevData,
      [name]: processedValue,
    }));

    setError(null);
  }, []);

  const validateForm = useCallback(() => {
    if (!formData.descricao || formData.descricao.trim() === '') {
      setError('Descrição é obrigatória');
      return false;
    }
    if (formData.descricao.trim().length < 2) {
      setError('Descrição deve ter pelo menos 2 caracteres');
      return false;
    }
    if (formData.descricao.trim().length > 100) {
      setError('Descrição deve ter no máximo 100 caracteres');
      return false;
    }
    return true;
  }, [formData, isEditing]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        const dataToSend = {
          descricao: formData.descricao.trim(),
        };

        if (isEditing) {
          // Incluir o ID apenas na edição
          dataToSend.id = tipoObjeto.id;
          await axios.put(
            `${API_BASE_URL.TIPO_OBJETO}/${tipoObjeto.id}`,
            dataToSend,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        } else {
          // Na criação, não enviar ID - o backend gera automaticamente
          await axios.post(`${API_BASE_URL.TIPO_OBJETO}/save`, dataToSend, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }

        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } catch (error) {
        console.error('Erro ao salvar tipo de objeto:', error);

        if (error.response?.status === 409) {
          setError('ID já existe. Escolha outro ID.');
        } else if (error.response?.status === 400) {
          setError(error.response?.data?.message || 'Dados inválidos');
        } else {
          setError('Erro ao salvar tipo de objeto. Tente novamente.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, validateForm, token, onSuccess, onClose, isEditing, tipoObjeto]
  );

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <>
      <Modal show={isOpen} onHide={handleClose} backdrop="static">
        <div className="modal-header text-white py-1" style={{ background: 'linear-gradient(90deg, #0b3383, #0e48ad)' }}>
          <h6 className="modal-title text-center m-0">
            <i
              className={`bi ${
                isEditing ? 'bi-pencil-square' : 'bi-plus-square'
              } me-2`}
            ></i>
            {isEditing ? 'Editar Tipo de Objeto' : 'Novo Tipo de Objeto'}
          </h6>
          <button
            className="btn btn-link text-white p-0 mb-0"
            onClick={handleClose}
            title="Fechar"
            style={{ fontSize: '0.9rem', textDecoration: 'none' }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Form onSubmit={handleSubmit} key={tipoObjeto?.id || 'new'}>
          <Modal.Body>
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            <Row className="g-3">
              <Col md={12}>
                <Form.Group className="mb-1">
                  <Form.Label>Descrição *</Form.Label>
                  <Form.Control
                    type="text"
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleInputChange}
                    size="sm"
                    required
                    minLength={2}
                    maxLength={100}
                    placeholder="Descrição do tipo de objeto"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Salvando...
                </>
              ) : isEditing ? (
                'Atualizar'
              ) : (
                'Salvar'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

TipoObjetoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  tipoObjeto: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default TipoObjetoModal;
